import { Stack, Text, Center, Group, TextInput, Button } from '@mantine/core';
import { Database, Search, X, Download } from 'lucide-react';
import { useState, useMemo } from 'react';
import { CoachPostSessionTable } from './CoachPostSessionTable';
import { useDataLibraryCoachPostSession } from './useDataLibraryCoachPostSession';
import { FilterPanel } from '../../common/FilterPanel/FilterPanel';
import type { FilterFieldConfig, QuickFilterConfig } from '../../common/FilterPanel/FilterPanel';
import { SortPanel } from '../../common/SortPanel/SortPanel';
import type { SortFieldConfig } from '../../common/SortPanel/SortPanel';
import { GroupPanel } from '../../common/GroupPanel/GroupPanel';
import { SavedViewsMenu } from '../components/SavedViewsMenu';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { deriveGroupFields } from '../../../utils/groupDataByField';
import { ColumnSettingsPopover } from '../../common/ColumnSettingsPopover/ColumnSettingsPopover';
import { ExportModal } from '../../common/ExportModal/ExportModal';
import { exportSummaryProps, splitScreenFilters } from '../../common/ExportModal/filterSummary';
import type { SortState } from '../../../utils/sortTasks';
import { clearThenFocusFilter } from '../../../utils/focusFilterTrigger';

interface DataLibraryCoachPostSessionTabProps {
  selectedCohortIds: string[];
  activeGroup: GroupState | null;
  onGroupChange: (group: GroupState | null) => void;
}

const SORT_FIELDS: SortFieldConfig[] = [
  { value: 'session_number', label: 'Session Number' },
  { value: 'session_date', label: 'Session Date' },
  { value: 'coach_name', label: 'Coach Name' },
  { value: 'date_submitted', label: 'Date Submitted' },
];

const QUICK_FILTERS: QuickFilterConfig[] = [
  { key: 'missing_feedback', label: 'Missing Feedback' },
  { key: 'has_flags', label: 'Has Flags' },
];

export function DataLibraryCoachPostSessionTab({
  selectedCohortIds,
  activeGroup,
  onGroupChange,
}: DataLibraryCoachPostSessionTabProps) {
  const {
    rows,
    groupedRows,
    questions,
    loading,
    totalCount,
    averages,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize,
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    activeQuickFilters,
    handleQuickFilterToggle,
    clearAllFilters,
    hasActiveFilters,
    columns,
    setColumns,
    visibleColumns,
    groupLimitTotal,
  } = useDataLibraryCoachPostSession({ selectedCohortIds, activeGroup, onGroupChange });

  const [exportOpen, setExportOpen] = useState(false);
  const groupFields = useMemo(() => deriveGroupFields(columns), [columns]);

  const filterFields: FilterFieldConfig[] = useMemo(() => {
    const fields: FilterFieldConfig[] = [];
    if (filterOptions.cohorts.length > 1) {
      fields.push({
        key: 'cohort',
        label: 'Cohort',
        type: 'select' as const,
        options: filterOptions.cohorts,
      });
    }
    if (filterOptions.groups.length > 0) {
      fields.push({
        key: 'group',
        label: 'Group',
        type: 'multiselect' as const,
        options: filterOptions.groups,
      });
    }
    if (filterOptions.coaches.length > 0) {
      fields.push({
        key: 'coach',
        label: 'Coach',
        type: 'select' as const,
        options: filterOptions.coaches,
      });
    }
    if (filterOptions.sessionNumbers.length > 0) {
      fields.push({
        key: 'session_number',
        label: 'Session Number',
        type: 'multiselect' as const,
        options: filterOptions.sessionNumbers,
      });
    }
    return fields;
  }, [filterOptions]);

  const activeSortState: SortState | null = sortConfig
    ? { field: sortConfig.key, direction: sortConfig.order === 'ASC' ? 'asc' : 'desc' }
    : null;

  if (selectedCohortIds.length === 0) {
    return (
      <Center h={300}>
        <Stack align="center" gap="sm">
          <Database size={40} color="#888" />
          <Text c="dimmed" size="lg">
            Select one or more cohorts to view coach post-session feedback
          </Text>
          <Text c="dimmed" size="sm">
            Use the side panel to choose cohorts
          </Text>
        </Stack>
      </Center>
    );
  }

  const hidePagination = !!groupedRows;

  return (
    <Stack gap="sm">
      {/* Toolbar */}
      <Group justify="space-between" mb="sm" wrap="wrap" gap="xs">
        <Group gap="xs" wrap="wrap">
          <TextInput
            placeholder="Search coach sessions..."
            size="sm"
            leftSection={<Search size={14} />}
            rightSection={
              searchValue ? (
                <X size={14} style={{ cursor: 'pointer' }} onClick={() => setSearchValue('')} />
              ) : null
            }
            value={searchValue}
            onChange={e => setSearchValue(e.currentTarget.value)}
            w={220}
          />

          <FilterPanel
            quickFilters={QUICK_FILTERS}
            activeQuickFilters={activeQuickFilters}
            onQuickFilterToggle={handleQuickFilterToggle}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterAdd={handleFilterAdd}
            onFilterChange={handleFilterChange}
            onFilterRemove={handleFilterRemove}
            onClearAll={clearAllFilters}
            size="sm"
          />

          <SortPanel
            sortFields={SORT_FIELDS}
            activeSort={activeSortState}
            defaultSort={{ field: 'session_number', direction: 'asc' }}
            onSortChange={handleSortPanelChange as (sort: SortState | null) => void}
            size="sm"
          />

          <GroupPanel
            limitTotal={groupLimitTotal}
            groupFields={groupFields}
            activeGroup={activeGroup}
            onGroupChange={onGroupChange}
            size="sm"
          />

          <SavedViewsMenu
            columns={columns}
            onColumnsChange={setColumns}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterAdd={handleFilterAdd}
            onFilterChange={handleFilterChange}
            onFilterRemove={handleFilterRemove}
            quickFilters={QUICK_FILTERS}
            activeQuickFilters={activeQuickFilters}
            onQuickFilterToggle={handleQuickFilterToggle}
            sortFields={SORT_FIELDS}
            activeSort={activeSortState}
            onSortChange={handleSortPanelChange as (sort: SortState | null) => void}
            groupFields={groupFields}
            activeGroup={activeGroup}
            onGroupChange={onGroupChange}
            size="sm"
          />

          {hasActiveFilters && (
            <Button variant="subtle" size="sm" color="gray" leftSection={<X size={14} />} onClick={clearThenFocusFilter(clearAllFilters)}>
              Clear
            </Button>
          )}
        </Group>

        <Group gap="xs">
          <Text size="sm" c="dimmed">
            {totalCount} session{totalCount !== 1 ? 's' : ''}
          </Text>
          <ColumnSettingsPopover columns={columns} onColumnsChange={setColumns} zIndex={1000} />
          <Button
            variant="outline"
            color="gray"
            bg="white"
            size="sm"
            style={{ borderColor: '#E8E6E3' }}
            leftSection={<Download size={14} />}
            onClick={() => setExportOpen(true)}
          >
            Export
          </Button>
        </Group>
      </Group>

      {/* MED-1296: the grid is empty only when the cohort has no completed group
          sessions — every completed session renders a row regardless of whether
          feedback was submitted (blank cells when none), so the old "no feedback
          yet" named the wrong cause. `hasSurveyConfig` is deliberately NOT used to
          gate a "not configured" message the way the sibling survey tabs do: coach
          post-session feedback comes from the in-app form, never a
          feedback_survey_config, so that flag is structurally always false here and
          would mislabel every empty cohort. MDF-39: with a filter or search set, the shared empty state says no records match. */}
      <CoachPostSessionTable
        data={rows}
        averages={averages}
        groupedData={groupedRows}
        columns={visibleColumns}
        questions={questions}
        sortConfig={sortConfig}
        onSort={handleSort}
        loading={loading}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearAllFilters}
        emptyMessage="No completed group sessions yet"
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        hidePagination={hidePagination}
      />

      <ExportModal
        opened={exportOpen}
        onClose={() => setExportOpen(false)}
        entityType="survey_responses"
        contextType="global"
        scopeContext={{ cohortIds: selectedCohortIds, surveyKind: 'coach_post_session' }}
        filterContext={{ groupIds: activeFilters.find(f => f.key === 'group')?.values }}
        {...exportSummaryProps(
          splitScreenFilters({ search: searchValue, quickFilters: QUICK_FILTERS, activeQuickFilters, fields: filterFields, rows: activeFilters }, ['group']),
        )}
        currentViewColumns={columns.filter(c => c.visible).map(c => c.id)}
        gridColumns={columns}
        recordCount={totalCount}
      />
    </Stack>
  );
}
